import type { GeometryElement, Point } from 'jsxgraph';
import type { ThemeColorName } from '@examdojo/styles';
import {
  bindGrabHandle,
  bindLabel,
  type BoundGrabHandle,
  buildSnappingAttributes,
  createPointLabelContext,
  resolveLabelConfig,
} from '@examdojo/core/widget';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType, HandleBehavior } from '../../../graphpad.model';
import type { CreatedElement, ElementContext, FunctionElement } from '../../element.model';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import type { HandleParams } from './handle.model';

/** Callback type for when handle drag updates parameters */
export type HandleDragCallback = (elementId: string, params: Record<string, number>) => void;

/**
 * HandleCreator exposes and styles the internal control points of functions.
 *
 * When a handle is dragged, it updates the parent function's params via setParams(),
 * which updates the underlying JsxParamStore (source of truth). This ensures:
 * - The curve auto-updates via JSXGraph's dependency graph
 * - Angular sliders can sync by reading the updated param values
 *
 * It does NOT create new points - it makes existing function handles visible,
 * applies styling from the display config, and sets up drag behavior
 * based on the behavior type.
 *
 * Default styling:
 * - Fill color matches the parent curve (via display config color)
 * - White stroke outline for visibility
 * - Drop shadow for depth
 * - Size 8 for easy interaction
 */
export class HandleCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'handle';

  /** Optional callback to notify when handle drag updates params */
  private onDragCallback?: HandleDragCallback;

  /** Set the callback for drag events */
  setOnDragCallback(callback: HandleDragCallback): void {
    this.onDragCallback = callback;
  }

  create(
    id: string,
    def: FeatureElementDef<'handle'>,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { themeService } = context;

    // Extract params
    const params = def.params as HandleParams | undefined;
    if (!params?.of || !params?.behavior) {
      throw new Error('Handle requires params.of and params.behavior to be specified');
    }

    // Get the parent function
    const parentFn: FunctionElement = this.getParentFunction(params.of, context);

    // Get the existing handle from the function
    const existingHandle = parentFn.handles.get(params.behavior);
    if (!existingHandle) {
      throw new Error(`Function '${params.of}' doesn't have a handle for behavior: ${params.behavior}`);
    }

    // Resolve point style using the feature style cascade (label and snapping are now part of style)
    const pointStyle = resolveFeatureStyle('point', this.featureType, context, display);
    const visible = resolveFeatureVisibility(this.featureType, context.featureTheme, display);

    // Resolve colors - fill color from display config (matches curve), stroke is white by default
    const fillColorName = (display?.point?.color ?? pointStyle.color ?? 'sky') as ThemeColorName;
    const fillColor = themeService.getColor(fillColorName);
    // Default stroke to white (neutral-0) - use CSS variable directly since it's not a ThemeColorName
    const strokeColor = pointStyle.strokeColor ? themeService.getColor(pointStyle.strokeColor) : 'var(--neutral-0)';
    const size = pointStyle.size ?? 8;

    // Build snapping attributes from style (snapping is now part of point style)
    const snappingAttrs = buildSnappingAttributes(
      { board: context.board, container: context.container },
      pointStyle.snapping,
    );

    // Make the handle visible and draggable, apply styling and snapping
    // Handles have curve-matching fill with white stroke outline
    existingHandle.setAttribute({
      visible: visible !== false && display?.visible !== false,
      fixed: false,
      size,
      fillColor,
      strokeColor,
      strokeWidth: pointStyle.strokeWidth ?? 2,
      ...snappingAttrs,
    });

    // Set up drag behavior - updates parent params and notifies callback
    this.setupDragBehavior(existingHandle, params.behavior, params.of, parentFn);

    const objects: GeometryElement[] = [existingHandle as GeometryElement];

    // Create grab handle if enabled (default: true for handles)
    let grabHandle: BoundGrabHandle | undefined;
    const showGrabHandle = pointStyle.showGrabHandle ?? display?.point?.showGrabHandle;
    if (showGrabHandle !== false) {
      grabHandle = bindGrabHandle({
        ctx: { board: context.board, container: context.container },
        point: existingHandle,
        color: fillColor,
        snapping: pointStyle.snapping,
        onDrag: () => {
          // Trigger the same param update logic as point drag
          this.handleGrabHandleDrag(existingHandle, params.behavior, params.of, parentFn);
        },
      });
      objects.push(grabHandle.element);
    }

    // Create label if configured (label is now part of style)
    const resolvedLabel = resolveLabelConfig(pointStyle.label, {
      color: fillColorName,
    });

    let labelElement: GeometryElement | undefined;
    if (resolvedLabel) {
      labelElement = bindLabel({
        ctx: { board: context.board, container: context.container },
        themeService: context.themeService,
        element: existingHandle as GeometryElement,
        config: resolvedLabel,
        contextProvider: createPointLabelContext('', existingHandle as GeometryElement),
      });
      objects.push(labelElement);
    }

    return {
      id,
      type: 'handle',
      objects,
      destroy: () => {
        // Don't remove the point - just hide it and fix it, as it's owned by the function
        existingHandle.setAttribute({
          visible: false,
          fixed: true,
        });
        // Remove the grab handle if it exists
        grabHandle?.destroy();
        // Remove the label if it exists
        if (labelElement) {
          context.board.removeObject(labelElement);
        }
      },
    };
  }

  /**
   * Set up drag behavior for the handle.
   * When dragged, updates the parent function's params via setParams().
   */
  private setupDragBehavior(
    handle: Point,
    behavior: HandleBehavior,
    parentId: string,
    parentFn: FunctionElement,
  ): void {
    handle.on('drag', () => {
      this.handleGrabHandleDrag(handle, behavior, parentId, parentFn);
    });
  }

  /**
   * Handle drag updates from either the point or the grab handle.
   * Updates the parent function's params via setParams().
   * Uses fromPosition transform if available for proper coordinate mapping.
   */
  private handleGrabHandleDrag(
    handle: Point,
    behavior: HandleBehavior,
    parentId: string,
    parentFn: FunctionElement,
  ): void {
    const config = parentFn.handleConfigs[behavior];
    if (!config) {
      throw new Error(`No handle config for behavior: ${behavior}`);
    }

    const x = handle.X();
    const y = handle.Y();
    const currentParams = parentFn.getParams();
    const { xParam, yParam } = config;

    // Use fromPosition transform if available
    if (config.fromPosition) {
      const newParams = config.fromPosition(x, y, currentParams);
      switch (behavior) {
        case 'translate': {
          parentFn.setParams(newParams);
          break;
        }
        case 'translate-x': {
          parentFn.setParams({ [xParam]: newParams[xParam] });
          // Keep handle on the curve by recomputing position
          if (config.toPosition) {
            const updatedParams = { ...currentParams, [xParam]: newParams[xParam] };
            const [newX, newY] = config.toPosition(updatedParams);
            (
              handle as unknown as { setPositionDirectly: (type: number, coords: [number, number]) => void }
            ).setPositionDirectly(1, [newX, newY]);
          }
          break;
        }
        case 'translate-y': {
          parentFn.setParams({ [yParam]: newParams[yParam] });
          // Keep handle on the curve by recomputing position
          if (config.toPosition) {
            const updatedParams = { ...currentParams, [yParam]: newParams[yParam] };
            const [newX, newY] = config.toPosition(updatedParams);
            (
              handle as unknown as { setPositionDirectly: (type: number, coords: [number, number]) => void }
            ).setPositionDirectly(1, [newX, newY]);
          }
          break;
        }
      }
    } else {
      // Default behavior: direct mapping
      switch (behavior) {
        case 'translate': {
          parentFn.setParams({ [xParam]: x, [yParam]: y });
          break;
        }
        case 'translate-x': {
          parentFn.setParams({ [xParam]: x });
          const fixedY = currentParams[yParam] ?? y;
          (
            handle as unknown as { setPositionDirectly: (type: number, coords: [number, number]) => void }
          ).setPositionDirectly(1, [x, fixedY]);
          break;
        }
        case 'translate-y': {
          parentFn.setParams({ [yParam]: y });
          const fixedX = currentParams[xParam] ?? x;
          (
            handle as unknown as { setPositionDirectly: (type: number, coords: [number, number]) => void }
          ).setPositionDirectly(1, [fixedX, y]);
          break;
        }
      }
    }

    if (this.onDragCallback) {
      this.onDragCallback(parentId, parentFn.getParams());
    }
  }
}
